<script setup>
import { computed, ref, watch } from "vue";
import { useActivityStore } from "@/stores/useActivityStore";
import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
import UserCardModal from "@/components/User/UserCardModal.vue";
import TextCardModal from "@/components/Text/TextCardModal.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";
import EditActivityForm from "@/components/Activity/EditActivityForm.vue";
import DeleteActivityForm from "@/components/Activity/DeleteActivityForm.vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import {
  progressShortLabel,
  progressFullLabel,
} from "@/utils/activityProgress";
import { applyReadingAttrs } from "@/utils/htmlReadingAttrs";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  activityId: { type: Number, required: true },
});

const emit = defineEmits(["close", "activity-updated", "notify"]);

const activityStore = useActivityStore();
const textStore = useTextStore();
const userStore = useUserStore();

const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const showUserModal = ref(false);
const selectedUserId = ref(null);

const showPermissionView = ref(false);

// Modal de contexto do texto
const showTextCardModal = ref(false);
const selectedTextIdForModal = ref(null);

// Dados do breadcrumb/contexto do texto
const breadcrumbTextTitle = ref("");
const breadcrumbTextSubtitle = ref("");
const linkedTextId = ref(null);

// Estado para controle do EditActivityForm
const showEditModal = ref(false);

// Estado para controle do DeleteActivityForm
const showDeleteModal = ref(false);

// ✅ FIX: durante a exclusão a atividade some do store ANTES de o card fechar.
// Congelamos o último estado para o card permanecer CHEIO até sumir (sem piscar vazio).
const isDeletingActivity = ref(false);
const frozenActivity = ref(null);

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

function openPermissionView() {
  showPermissionView.value = true;
}

function closePermissionView() {
  showPermissionView.value = false;
}

function openTextCard() {
  const id = linkedTextId.value ?? activity.value?.textId ?? null;
  if (!id) return;
  selectedTextIdForModal.value = id;
  showTextCardModal.value = true;
}

function closeTextCard() {
  showTextCardModal.value = false;
  selectedTextIdForModal.value = null;
}

// Permissão de edição/exclusão
const canEdit = computed(() => {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;
  if (logged === "sys") return true;
  return activity.value?.createdByUserId === userStore.currentUser?.userId;
});

function openEditModal() {
  activityStore.setIdActivity(props.activityId);
  showEditModal.value = true;
}

function handleEditRefresh() {
  activityStore.getActivityById(props.activityId);
  emit("activity-updated");
}

function openDeleteModal() {
  // ✅ FIX: congela o estado atual antes de abrir a confirmação, para o card não
  // piscar vazio quando a atividade sair do store durante a exclusão.
  frozenActivity.value = activity.value ? { ...activity.value } : null;
  isDeletingActivity.value = true;
  showDeleteModal.value = true;
}

function handleDeleteClose() {
  showDeleteModal.value = false;
  emit("activity-updated");
  emit("close");
}

// ✅ FIX: o DeleteActivityForm emite "notify" de sucesso (e depois refresh/close) de
// forma síncrona logo após o delete. Repassamos esse notify ao pai ANTES de o card
// fechar — é o pai (ActivityGeneralForm / NoteGeneralForm) que exibe o toast, pois ao
// fechar o card o toast interno do DeleteConfirmModal é desmontado.
// Erros também são repassados; o pai ignora erro (DeleteConfirmModal já mostra o erro
// e mantém o card aberto), evitando duplicidade.
function handleDeleteNotify(payload) {
  emit("notify", payload);
}

const currentUserId = computed(() => userStore.currentUser?.userId ?? null);

const activity = computed(() => {
  const selected = activityStore.selectedActivity;
  if (selected && selected.activityId === props.activityId) {
    return selected;
  }

  const live =
    activityStore.activities.find((a) => a.activityId === props.activityId) ||
    null;
  if (live) return live;

  // ✅ FIX: atividade já removida do store, mas exclusão em andamento → usa o snapshot
  // congelado para o card continuar cheio até o fechamento. (Só entra quando a atividade
  // realmente sumiu; em cancelamento ela ainda existe e prevalece o dado vivo.)
  if (isDeletingActivity.value) return frozenActivity.value;

  return null;
});

const totalNotes = computed(() => {
  const selected = activityStore.selectedActivity;
  if (
    selected &&
    selected.activityId === props.activityId &&
    Array.isArray(selected.allNotes)
  ) {
    return selected.allNotes.length;
  }
  if (!activity.value) return 0;
  return (activity.value.noteCount || 0) + (activity.value.commentCount || 0);
});

const canViewPermissions = computed(() => {
  const userId = Number(currentUserId.value);
  if (!userId || !activity.value) return false;
  return (
    userId === Number(activity.value.createdByUserId) ||
    userId === Number(activity.value.textCreatedByUserId)
  );
});

async function fetchRelatedTextData(activityData) {
  if (!activityData) return;

  linkedTextId.value = activityData.textId ?? null;
  breadcrumbTextTitle.value =
    activityData.textTitle ?? activityData.titleText ?? "";
  breadcrumbTextSubtitle.value =
    activityData.textSubtitle ?? activityData.subtitleText ?? "";

  if (linkedTextId.value && linkedTextId.value > 0) {
    const text = await textStore.getTextById(linkedTextId.value);
    if (text) {
      if (!breadcrumbTextTitle.value) {
        breadcrumbTextTitle.value = text.title ?? text.textTitle ?? "";
      }
      if (!breadcrumbTextSubtitle.value) {
        breadcrumbTextSubtitle.value = text.subtitle ?? text.textSubtitle ?? "";
      }
    }
  }
}

const hasLinkedText = computed(() => {
  return !!String(breadcrumbTextTitle.value ?? "").trim();
});

const formattedCreatedAt = computed(() => {
  if (!activity.value?.createdAt) return "";
  return formatDateTimeForDisplay(activity.value.createdAt);
});

const formattedUpdatedAt = computed(() => {
  if (!activity.value?.updatedAt) return "";
  return formatDateTimeForDisplay(activity.value.updatedAt);
});

watch(
  () => props.activityId,
  async (newId) => {
    if (!newId) return;

    isLoadingData.value = true;

    breadcrumbTextTitle.value = "";
    breadcrumbTextSubtitle.value = "";
    linkedTextId.value = null;

    try {
      const fetchedActivity = await activityStore.getActivityById(newId);
      await fetchRelatedTextData(fetchedActivity || activity.value);
    } catch (e) {
      console.error("Erro ao carregar atividade no ActivityCardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

const isDescriptionHtml = computed(() => activity.value?.contentFormat === "html");

const descriptionBody = computed(() => {
  return activity.value?.description || "Descrição não informada.";
});

const preparedDescriptionHtml = computed(() =>
  isDescriptionHtml.value ? applyReadingAttrs(activity.value?.description) : ""
);

function normalizeAvatarPath(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="activity-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes da Atividade</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="activity && !isLoadingData" class="card-content-grid">
        <div class="activity-details-col">
          <!-- Título + botões editar/apagar -->
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ activity.title }}
              <span class="title-id">(Id: {{ activity.activityId }})</span>
            </h3>

            <div class="title-actions">
              <button
                v-if="canEdit"
                type="button"
                class="btn-edit-activity tooltip-bottom"
                data-tooltip="Editar atividade"
                @click.stop="openEditModal"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>

              <button
                v-if="canEdit"
                type="button"
                class="btn-delete-activity tooltip-bottom"
                data-tooltip="Apagar atividade"
                @click.stop="openDeleteModal"
              >
                <i class="fas fa-trash-alt"></i>
              </button>

              <button
                v-if="canViewPermissions"
                type="button"
                class="btn-view-permissions-activity tooltip-bottom"
                data-tooltip="Ver permissões"
                @click.stop="openPermissionView"
              >
                <i class="fas fa-user-shield"></i>
              </button>
            </div>
          </div>

          <div class="meta-grid meta-grid-spaced">
            <div class="meta-item">
              <span class="meta-label">Status</span>
              <span class="meta-value">
                <span
                  :class="[
 'status-chip',
                    activity.active
                      ? 'status-chip--active'
                      : 'status-chip--inactive',
                  ]"
                >
                  {{ activity.active ? "Ativo" : "Inativo" }}
                </span>
              </span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Progresso</span>
              <span
                class="meta-value"
                :title="progressFullLabel(activity.status)"
                >{{ progressShortLabel(activity.status) }}</span
              >
            </div>

            <div class="meta-item">
              <span class="meta-label">Qtd Notas</span>
              <div class="mt-1">
                <span
                  class="chips-note-count"
                  :class="{ 'active-notes': totalNotes > 0 }"
                >
                  {{ totalNotes }}
                </span>
              </div>
            </div>

            <div class="meta-item meta-item-full">
              <div class="date-grid">
                <div class="date-block">
                  <span class="meta-label">Data Início</span>
                  <span class="meta-value">
                    {{
                      formatDateTimeForDisplay(activity.startedAt) ||
                      "Não informada"
                    }}
                  </span>
                </div>
                <div class="date-block">
                  <span class="meta-label">Data Fim</span>
                  <span class="meta-value">
                    {{
                      formatDateTimeForDisplay(activity.endedAt) ||
                      "Não informada"
                    }}
                  </span>
                </div>
                <div v-if="activity.plannedStartAt" class="date-block">
                  <span class="meta-label">Início previsto</span>
                  <span class="meta-value">{{ formatDateTimeForDisplay(activity.plannedStartAt) }}</span>
                </div>
                <div v-if="activity.plannedEndAt" class="date-block">
                  <span class="meta-label">Fim previsto</span>
                  <span class="meta-value">{{ formatDateTimeForDisplay(activity.plannedEndAt) }}</span>
                </div>
              </div>
            </div>
          </div>

          <hr class="content-separator" />

          <div v-if="hasLinkedText" class="context-block">
            <span class="meta-label">TEXTO</span>

            <div class="footer-context-row">
              <button
                type="button"
                class="meta-chip meta-chip-text meta-chip-link"
                :title="
                  breadcrumbTextSubtitle
                    ? `${breadcrumbTextTitle}\n(${breadcrumbTextSubtitle})`
                    : breadcrumbTextTitle
                "
                @click.stop="openTextCard"
              >
                <i class="fas fa-book context-icon"></i>
                <span class="meta-chip-label">{{ breadcrumbTextTitle }}</span>
              </button>
            </div>
          </div>

          <div class="footer-info">
            <div class="users-row">
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(activity.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getAvatarUrl(activity.createdByUserPhotoPath)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        activity.createdByUserPhotoPath
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ activity.createdByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedCreatedAt" class="chip-date">
                    {{ formattedCreatedAt }}
                  </span>
                </div>
              </div>

              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(activity.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getAvatarUrl(activity.updatedByUserPhotoPath)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        activity.updatedByUserPhotoPath
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ activity.updatedByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedUpdatedAt" class="chip-date">
                    {{ formattedUpdatedAt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="selected-text-col">
          <label class="form-label text-label">Descrição da Atividade</label>
          <blockquote
            v-if="isDescriptionHtml && preparedDescriptionHtml"
            class="themed-blockquote themed-blockquote--html p-4 border-l-4 rounded break-words"
          >
            <!-- Seguro: a descrição chega já normalizada e filtrada pela
                 whitelist do HtmlNormalizer (backend) antes de ser gravada —
                 não há offset de nota ancorado nela, então não precisa do
                 renderizador recursivo que o Texto usa. O `applyReadingAttrs`
                 repõe o que esse atalho perderia: link externo em aba nova,
                 imagem sem vazar o Referer. -->
            <div class="text-body--html" v-html="preparedDescriptionHtml"></div>
          </blockquote>
          <blockquote
            v-else
            class="themed-blockquote p-4 border-l-4 rounded whitespace-pre-wrap break-words font-mono"
          >
            {{ descriptionBody }}
          </blockquote>
        </div>
      </div>

      <div v-else class="card-body loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes da
        atividade...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <TextCardModal
      v-if="showTextCardModal"
      :show="showTextCardModal"
      :textId="selectedTextIdForModal"
      @close="closeTextCard"
    />

    <PermissionView
      v-if="showPermissionView"
      :show="showPermissionView"
      entityType="activity"
      :activityId="props.activityId"
      title="Permissões da atividade"
      @close="closePermissionView"
    />

    <EditActivityForm
      v-if="showEditModal"
      :activityId="props.activityId"
      :textId="activity?.textId ?? 0"
      @close="showEditModal = false"
      @refresh="handleEditRefresh"
    />
    <DeleteActivityForm
      v-if="showDeleteModal && activity"
      :activity="activity"
      @close="handleDeleteClose"
      @refresh="handleDeleteClose"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.activity-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  display: flex;
  padding: 1rem;
  gap: 1.25rem;
}

.activity-details-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 1rem;
  border-right: 1px solid var(--border-color);
}

.selected-text-col {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 12px;
}

.title-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

/* ── Botão Editar ── */
.btn-edit-activity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-edit-activity i {
  font-size: 0.6rem;
}
.btn-edit-activity:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

/* ── Botão Apagar ── */
.btn-delete-activity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-delete-activity i {
  font-size: 0.6rem;
}
.btn-delete-activity:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

/* ── Botão Ver permissões ── */
.btn-view-permissions-activity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--border-color);
  background-color: var(--neutral-100);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-view-permissions-activity i {
  font-size: 0.6rem;
}
.btn-view-permissions-activity:hover {
  background-color: var(--border-color);
  color: var(--text-dark);
  transform: translateY(-1px);
}

/* Tooltip */
.tooltip-bottom {
  position: relative;
}
.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}
.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.meta-grid-spaced {
  gap: 12px 14px;
}

.meta-label {
  margin-bottom: 2px;
}

.date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.date-block {
  display: flex;
  flex-direction: column;
}

.chips-note-count {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
  background-color: var(--neutral-200);
  transition: all 0.2s;
}

.chips-note-count.active-notes {
  background-color: #ccf7ff;
  color: #1a4a4f;
}

.context-block {
  margin-bottom: 12px;
}

.footer-context-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.meta-chip {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  padding: 1px 0;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 500;
  background: transparent !important;
  border: none;
  opacity: 0.82;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.meta-chip-label {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
}

.meta-chip-text {
  color: var(--text-muted);
}

.meta-chip-link {
  cursor: pointer;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  text-align: left;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.meta-chip-link:hover {
  opacity: 1;
  transform: translateY(-1px);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.context-icon {
  font-size: 0.7rem;
  margin-right: 4px;
  position: relative;
  top: 5px;
  opacity: 0.75;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

.text-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
  margin-bottom: 0.3rem;
  display: block;
}

.themed-blockquote {
  background-color: var(--bg-header);
  border-left: 4px solid var(--brand-light);
  color: var(--text-dark);
  padding: 1rem;
  border-radius: 8px;
  line-height: 1.35;
  height: 380px;
  max-height: 640px;
  overflow-y: auto;
  box-sizing: border-box;
  font-size: 0.9rem;
}

</style>
